import { Flex } from 'antd';
import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useMemo, useState } from 'react';

import type { AnalyticsDialogsRequest, AnalyticsWidgetStatsRequest } from '@shared/api';
import type { FilterOption } from '@shared/lib/types';
import type { DateFilterValue } from '@shared/ui/vanilla-calendar/lib/types';

import { FetchingErrorState } from '@shared/ui/fetching-error-state/fetching-error-state';
import { LoadingState } from '@shared/ui/loading-state/loading-state';

import { useLeadsStats } from './hooks/use-leads-stats';
import { useClientsStats } from './hooks/use-clients-stats';
import { useMessagesStats } from './hooks/use-messages-stats';
import { useAllStats } from './hooks/use-all-stats';
import { useWidgetStats } from './hooks/use-widget-stats';

import styles from './analytics.module.scss';
import { useTranslation } from 'react-i18next';

import {
  buildDefaultMonth,
  buildDefaultToday,
  buildDefaultWeek,
  buildDefaultYesterday,
  getIntervalForPeriod,
  mapIntervalsToChartData,
  type MetricKey,
} from './lib/helpers';

import { AnalyticsControls } from './ui/analytics-controls';
import type { PeriodPreset } from './ui/analytics-controls';
import { AnalyticsChartCard } from './ui/analytics-chart-card';
import { MetricSwitch } from './ui/metric-switch';
import { DialogSummary } from './ui/dialog-summary/dialog-summary';
import { useAppSelector } from '@shared/redux';
import { authSelectors } from '@shared/redux/ducks/auth';
import { PageHeader } from '@shared/ui/page-header';
import { WidgetAnalytics } from './ui/widget-analytics';
import { buildChangesDemo } from './lib/widget-changes-demo';
import {
  useGetWidgetChangesQuery,
  type WidgetChangesRequest,
} from '@shared/api/widget-changes-api';
import { useAnalyticsFilters, useAnalyticsView } from './hooks/use-analytics-filters';

export const Analytics = () => {
  const { t } = useTranslation('translation');

  const currentOrganization = useAppSelector(authSelectors.selectCurrentOrganizationInfo);
  const organizationId = currentOrganization?.id ?? 0;

  const [metric, setMetric] = useState<MetricKey>('clients');
  const { view, setView } = useAnalyticsView(organizationId);
  const defaultPeriodValue = useMemo(() => buildDefaultWeek(t('common.calendar.lastWeek')), [t]);
  const { filters, updateFilters } = useAnalyticsFilters(organizationId, view, defaultPeriodValue);
  const { periodPreset, periodValue, channelIds, pageUrlPatterns } = filters;
  const interval = getIntervalForPeriod(periodValue);

  const handleSelectPeriod = useCallback(
    (value: FilterOption<DateFilterValue>[]) => {
      updateFilters({ periodValue: value });
    },
    [updateFilters],
  );

  const handlePeriodPresetChange = useCallback(
    (next: PeriodPreset) => {
      let nextPeriodValue: FilterOption<DateFilterValue>[];

      if (next === 'today') {
        nextPeriodValue = buildDefaultToday(t('common.date.today'));
      } else if (next === 'yesterday') {
        nextPeriodValue = buildDefaultYesterday(t('common.date.yesterday'));
      } else if (next === 'month') {
        nextPeriodValue = buildDefaultMonth(t('common.calendar.lastMonth'));
      } else {
        nextPeriodValue = buildDefaultWeek(t('common.calendar.lastWeek'));
      }

      updateFilters({
        periodPreset: next,
        periodValue: nextPeriodValue,
      });
    },
    [t, updateFilters],
  );

  const handleChannelChange = useCallback(
    (values: number[]) => {
      updateFilters({ channelIds: values });
    },
    [updateFilters],
  );

  const channelValues = channelIds.length > 0 ? channelIds : undefined;

  const handlePageUrlPatternsChange = useCallback(
    (values: string[]) => {
      updateFilters({
        pageUrlPatterns: Array.from(new Set(values.map((value) => value.trim()).filter(Boolean))),
      });
    },
    [updateFilters],
  );

  const request = useMemo<AnalyticsDialogsRequest | null>(() => {
    const selected = periodValue?.[0]?.value;
    if (!selected?.startDate || !selected?.endDate || !organizationId) return null;

    return {
      channel_ids: channelIds?.length ? channelIds : null,
      date_from: selected.startDate,
      date_to: selected.endDate,
      organization_id: organizationId,
      interval,
    };
  }, [periodValue, interval, channelIds, organizationId]);

  const baseEnabled = Boolean(request);
  const overallEnabled = baseEnabled && view === 'overall';

  const leads = useLeadsStats(request, overallEnabled && metric === 'leads');
  const clients = useClientsStats(request, overallEnabled && metric === 'clients');
  const messages = useMessagesStats(request, overallEnabled && metric === 'messages');
  const allStats = useAllStats(request, overallEnabled);

  const widgetRequest = useMemo<AnalyticsWidgetStatsRequest | null>(
    () =>
      request
        ? {
            ...request,
            page_url_patterns: pageUrlPatterns.length ? pageUrlPatterns : null,
          }
        : null,
    [pageUrlPatterns, request],
  );
  const widgetStats = useWidgetStats(widgetRequest, baseEnabled && view === 'widget');
  const changesDemo = new URLSearchParams(window.location.search).get('changes_demo') === '1';
  const changesRequest = useMemo<WidgetChangesRequest | null>(
    () =>
      request && {
        organization_id: request.organization_id,
        channel_ids: request.channel_ids,
        date_from: request.date_from,
        date_to: request.date_to,
        interval: request.interval,
      },
    [request],
  );
  const widgetChanges = useGetWidgetChangesQuery(changesRequest ?? skipToken, {
    skip: view !== 'widget' || changesDemo,
    pollingInterval: 60_000,
  });
  const demoChanges = useMemo(
    () => (changesDemo && changesRequest ? buildChangesDemo(changesRequest) : undefined),
    [changesDemo, changesRequest],
  );

  const statsByMetric = useMemo(() => ({ messages, clients, leads }), [messages, clients, leads]);

  const activeStats = statsByMetric[metric];

  const isLoading = activeStats.isLoading;
  const isError = activeStats.isError;

  const refetchCurrent = useCallback(() => {
    activeStats.refetch();
  }, [activeStats]);

  const chartData = useMemo(
    () => mapIntervalsToChartData(activeStats.intervals, interval),
    [activeStats.intervals, interval],
  );

  const metricTitles: Record<MetricKey, string> = useMemo(
    () => ({
      clients: t('analytics.metrics.clients'),
      messages: t('analytics.metrics.messages'),
      leads: t('analytics.metrics.leads'),
    }),
    [t],
  );

  const metricTotals = useMemo(
    () => ({
      clients: allStats.data?.client_count ?? 0,
      messages: allStats.data?.message_count ?? 0,
      leads: allStats.data?.lead_count ?? 0,
    }),
    [allStats.data],
  );

  const metricOptions = useMemo(
    () => [
      {
        key: 'clients' as const,
        label: t('analytics.metricTabs.clients'),
        total: metricTotals.clients,
      },
      {
        key: 'messages' as const,
        label: t('analytics.metricTabs.messages'),
        total: metricTotals.messages,
      },
      {
        key: 'leads' as const,
        label: t('analytics.metricTabs.leads'),
        total: metricTotals.leads,
      },
    ],
    [metricTotals, t],
  );

  const handleMetricClick = useCallback(
    (next: MetricKey) => {
      if (next === metric) {
        activeStats.refetch();
        allStats.refetch();
        return;
      }
      setMetric(next);
    },
    [metric, activeStats, allStats],
  );

  return (
    <Flex vertical gap={20} className={styles.root}>
      <Flex vertical gap={20} className={styles.wrapper}>
        <PageHeader title={t('analytics.title')} />

        <Flex vertical gap={16} className={styles.container}>
          <AnalyticsControls
            view={view}
            onViewChange={setView}
            channelValues={channelValues}
            onChannelChange={handleChannelChange}
            pageUrlPatterns={pageUrlPatterns}
            onPageUrlPatternsChange={handlePageUrlPatternsChange}
            periodValue={periodValue}
            onPeriodChange={handleSelectPeriod}
            periodPreset={periodPreset}
            onPeriodPresetChange={handlePeriodPresetChange}
          />

          {view === 'overall' && (
            <>
              {isLoading && <LoadingState />}
              {!isLoading && isError && <FetchingErrorState refetch={refetchCurrent} />}

              {!isLoading && !isError && (
                <>
                  <AnalyticsChartCard
                    title={metricTitles[metric]}
                    total={activeStats.totalCount}
                    data={chartData}
                  />

                  <MetricSwitch
                    value={metric}
                    options={metricOptions}
                    onChange={handleMetricClick}
                  />
                </>
              )}

              <DialogSummary request={request} enabled={overallEnabled} />
            </>
          )}

          {view === 'widget' && (
            <WidgetAnalytics
              data={widgetStats.data}
              isLoading={widgetStats.isLoading}
              isError={widgetStats.isError}
              onRefetch={widgetStats.refetch}
              pagesRequest={widgetRequest}
              changesRequest={changesRequest}
              changes={demoChanges ?? widgetChanges.data}
              changesLoading={!changesDemo && widgetChanges.isLoading}
              changesError={!changesDemo && widgetChanges.isError}
              onChangesRefetch={widgetChanges.refetch}
              changesDemo={changesDemo}
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};
